import { TRANSFORM_LABELS } from '../cards/DrivenFromModal';
import { formatValue, getChoiceMeta } from './itemValueDisplay';

/*
 * The multi add page's "filled for you" rows (split out of MultiAddPage.js,
 * KPR-1725, which is held to its size by the file-size ratchet): what each
 * mapped element will carry, previewed as the read view would show it.
 * Pure: the page passes the form, its values and its switches.
 */

const isBlank = v => v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
const labelOf = element => element?.label?.singular || element?.name || '';

/** An ad hoc input as the element definition the renderer reads. */
export const inputAsElement = input => ({
	name: input.key,
	label: { singular: input.label || input.key, plural: input.label || input.key },
	dataType: input.dataType,
	options: input.options || {},
});

/** The step's name as the page says it: its label, else its card's name, else its key. */
export const stepLabelOf = (step, stepsMeta) => step?.label || stepsMeta?.[step?.key]?.card?.name || step?.key || '';

/**
 * The text a stored value reads as — the read view's formatting (dates,
 * measurements, choice labels, lookup titles through the page's resolver).
 * Empty string for a blank.
 */
const previewText = (element, value, { resolveLookup, prefs } = {}) => {
	if (isBlank(value)) return '';
	if (element?.dataType === 'card-lookup') {
		const resolved = typeof resolveLookup === 'function' ? resolveLookup(value) : '';
		return resolved || (Array.isArray(value) ? value.map(String).join(', ') : String(value));
	}
	const choice = getChoiceMeta(element, value);
	if (choice.label) return choice.label;
	const formatted = formatValue(element, value, prefs);
	if (Array.isArray(formatted)) return formatted.map(String).join(', ');
	if (formatted && typeof formatted === 'object') return JSON.stringify(formatted);
	return String(formatted ?? '');
};

/**
 * The value a step's element will carry, as the page can know it now: an
 * asked element's live value (first instance), an input's live value, a
 * fixed value, or — for a copy — the source step's element, one level down
 * (a copy of a copy resolves the same way; the API forbids cycles, and the
 * depth guard is belt and braces). Undefined for `step` and `omit`.
 */
const liveValueOf = (form, values, stepKey, name, depth = 0) => {
	if (depth > 8) return undefined;
	const step = (form?.steps || []).find(s => s.key === stepKey);
	const mapping = step?.fields?.[name];
	if (!mapping) return values?.steps?.[stepKey]?.[0]?.elements?.[name];
	if (mapping.from === 'static') return mapping.value;
	if (mapping.from === 'input') return values?.inputs?.[mapping.input];
	if (mapping.from === 'element') return liveValueOf(form, values, mapping.step, mapping.element, depth + 1);
	return undefined;
};

const transformNote = mapping => {
	const type = mapping?.transform?.type;
	return type && type !== 'none' ? (TRANSFORM_LABELS[type] || type) : null;
};

/**
 * The "filled for you" rows of one step: one per element the step fills,
 * with a read-only preview of what will land — the fixed value, the LIVE
 * input or source-step value formatted as the read view would show it,
 * or the item an earlier step creates. `muted` marks a placeholder (nothing
 * entered yet, a source step that is off); `note` is a conversion that
 * applies on the way in.
 *
 * `extras.stepsMeta` supplies the source steps' elements and cards;
 * `extras.resolveLookup` turns lookup ids into titles; `extras.prefs` is
 * the measurement display preference.
 */
export function filledRowsOf(step, meta, values, included, form, extras = {}) {
	const { stepsMeta, resolveLookup, prefs } = extras;
	const fmt = { resolveLookup, prefs };
	return (meta?.filled || []).map(element => {
		const mapping = step?.fields?.[element.name] || {};
		const row = { name: element.name, label: labelOf(element), text: '', muted: false, note: null };
		if (mapping.from === 'static') {
			row.text = previewText(element, mapping.value, fmt);
			if (!row.text) { row.text = 'Left blank'; row.muted = true; }
			return row;
		}
		if (mapping.from === 'input') {
			const input = (form?.inputs || []).find(i => i.key === mapping.input);
			row.text = previewText(input ? inputAsElement(input) : element, values?.inputs?.[mapping.input], fmt);
			if (!row.text) { row.text = 'Not entered yet'; row.muted = true; }
			row.note = transformNote(mapping);
			return row;
		}
		const source = (form?.steps || []).find(s => s.key === mapping.step);
		const sourceLabel = stepLabelOf(source, stepsMeta);
		if (mapping.from === 'element') {
			if (!source || !included?.[source.key]) {
				row.text = `Left out — the ${sourceLabel} step is off`;
				row.muted = true;
				return row;
			}
			const sourceElement = (stepsMeta?.[source.key]?.merged || []).find(el => el?.name === mapping.element) || element;
			row.text = previewText(sourceElement, liveValueOf(form, values, source.key, mapping.element), fmt);
			if (!row.text) { row.text = 'Not entered yet'; row.muted = true; }
			row.note = transformNote(mapping);
			return row;
		}
		if (mapping.from === 'step') {
			if (!source || !included?.[source.key]) {
				row.text = `Left out — the ${sourceLabel} step is off`;
				row.muted = true;
				return row;
			}
			const cardName = stepsMeta?.[source.key]?.card?.name || sourceLabel;
			if (source.repeat) {
				const count = (values?.steps?.[source.key] || []).length;
				row.text = `The ${cardName} items this form adds · all ${count}`;
			} else {
				row.text = `The ${cardName} item this form adds`;
			}
			return row;
		}
		row.text = 'Filled for you';
		row.muted = true;
		return row;
	});
}
